import { router, useLocalSearchParams } from "expo-router";
import { useMemo, useState } from "react";
import { Button } from "~/components/Button";
import { FieldGroup } from "~/components/form/FieldGroup";
import { BasicGallery } from "~/components/layout/gallery/BasicGallery";
import { ConfirmationModal } from "~/components/layout/modal/ConfirmationModal";
import { FullPageModal } from "~/components/layout/modal/FullPageModal";
import { Paragraphs } from "~/components/layout/text/Paragraphs";
import { Can } from "~/components/permission/Can";
import { Dismiss } from "~/components/permission/Dismiss";
import { Only } from "~/components/permission/Only";
import { useDismiss } from "~/providers/DismissProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { filled } from "~/util";

export default function InstructionDetails() {
    const { id }: { id: string } = useLocalSearchParams();
    const { handleDismiss } = useDismiss();
    const { instructions, can, destroyInstruction } = useProfile();
    const [confirm, setConfirm] = useState(false);
    const { addToast, saving, setSaving } = useFeedback();

    const instruction = useMemo(() => instructions.find((i) => i.id === id), [id, instructions]);

    const can_update = Boolean(instruction && can("update", "profile"));
    const can_delete = Boolean(can_update && !saving);

    const doDelete = () => {
        if (!can_delete) {
            return;
        }

        setSaving(true);

        destroyInstruction(id)
            .then(() => {
                addToast({
                    icon: "checkmark-circle",
                    content: "Instruction deleted",
                    duration: 3000,
                });

                router.dismissTo("/health-profile/emergency-instructions");
            })
            .finally(() => setSaving(false));
    };

    if (!instruction) {
        return <Dismiss />;
    }

    return (
        <FullPageModal
            withDone
            asRoute
            onRequestClose={handleDismiss}
            title="Emergency instruction"
            headerLeft={() => (
                <Can ability="update" section="profile">
                    <Button
                        variant="quiet"
                        title="Edit"
                        size="sm"
                        href={{ pathname: "/health-profile/emergency-instructions/[id]/edit", params: { id } }}
                    />
                </Can>
            )}
            footer={() => (
                <Can ability="update" section="profile">
                    <Button
                        icon="trash"
                        title="Delete instruction"
                        disabled={!can_delete}
                        variant="destructive"
                        size="lg"
                        onPress={() => setConfirm(true)}
                    />
                    <Only when={confirm}>
                        <ConfirmationModal
                            onConfirm={doDelete}
                            title="Are you sure you want to delete this instruction?"
                            confirmText="Delete instruction"
                            confirmVariant="destructive-quiet"
                            loading={filled(saving)}
                            onRequestClose={() => setConfirm(false)}
                        />
                    </Only>
                </Can>
            )}
        >
            <FieldGroup>
                <Paragraphs content={instruction?.description} />
            </FieldGroup>
            <BasicGallery images={instruction?.photograph_ids} />
        </FullPageModal>
    );
}
